import type { Asset } from '../types/ugla-types';
import type { LayerConfiguration, OptionConfiguration } from '../types/vap-types';
import { getAssetsByViewAndLayerAndRef, getBackgroundAssets } from './asset-helpers';
import {
  findAssetByProductOptions,
  getProductOptionsFromAsset,
  layerUsesProductOptions,
} from './product-options-helpers';
import { getProductLayer, getProductRef, getProductView } from './tag-helpers';

/**
 * Calculates which assets should be visible based on the current layer configuration
 * @param assets - All available assets
 * @param layerConfiguration - Current layer configuration (product_layer -> product_ref mapping)
 * @param optionConfiguration - Selected product_options values for option-enabled layers
 * @returns Array of assets that should be displayed
 */
export const getVisibleAssets = (
  assets: Asset[],
  layerConfiguration: LayerConfiguration,
  productView: string,
  optionConfiguration: OptionConfiguration = {},
): Asset[] => {
  const visibleAssets: Asset[] = [];

  // For each configured layer, get the assets for the selected product_ref or product_options
  Object.entries(layerConfiguration[productView]).forEach(([productLayer, productRef]) => {
    if (layerUsesProductOptions(assets, productLayer)) {
      const selectedOptions = optionConfiguration[productView]?.[productLayer];
      if (selectedOptions) {
        const matchingAsset = findAssetByProductOptions(
          assets,
          productView,
          productLayer,
          selectedOptions
        );
        if (matchingAsset) {
          visibleAssets.push(matchingAsset);
        }
      }
      return;
    }

    const layerAssets = getAssetsByViewAndLayerAndRef(assets, productView, productLayer, productRef);
    visibleAssets.push(...layerAssets);
  });

  return visibleAssets;
};

/**
 * Gets background assets (floors, walls, ...) for a specific view
 * @param assets - All available assets
 * @param productView - The product view to filter by
 * @returns Array of assets that should be displayed
 */
export const getBackgroundAssetsForView = (
  assets: Asset[],
  productView: string,
): Asset[] => {
  return getBackgroundAssets(assets).filter((asset) => {
    const view = getProductView(asset);
    return view === productView;
  });
};

/**
 * Gets assets that should be visible for a specific product layer
 * @param assets - All available assets
 * @param productView - The product view to filter by
 * @param productLayer - The product layer to filter by
 * @param layerConfiguration - Current layer configuration
 * @returns Array of assets for the specified layer
 */
export const getVisibleAssetsForLayer = (
  assets: Asset[],
  productView: string,
  productLayer: string,
  layerConfiguration: LayerConfiguration,
): Asset[] => {
  const productRef = layerConfiguration[productView][productLayer];
  if (!productRef) {
    return [];
  }

  return getAssetsByViewAndLayerAndRef(assets, productView, productLayer, productRef);
};

/**
 * Calculates z-index ordering for assets based on their product_layer
 * Lower layers (like floor) should have lower z-index, higher layers (like furniture) should have higher z-index
 * @param assets - Assets to order
 * @param layerOrder - Optional custom ordering of layers (if not provided, uses alphabetical)
 * @returns Map of asset URL to z-index value
 */
export const calculateZOrdering = (
  assets: Asset[],
  layerOrder?: string[]
): Map<string, number> => {
  const zOrderMap = new Map<string, number>();

  // Get unique product layers
  const layers = new Set<string>();
  assets.forEach((asset) => {
    const layer = getProductLayer(asset);
    if (layer) {
      layers.add(layer);
    }
  });

  // Create ordered list of layers
  const orderedLayers = layerOrder
    ? layerOrder.filter((layer) => layers.has(layer))
    : Array.from(layers).sort();

  // Assign z-index based on layer order
  orderedLayers.forEach((layer, index) => {
    assets.forEach((asset) => {
      const assetLayer = getProductLayer(asset);
      if (assetLayer === layer) {
        // Start z-index from 0, increment by 10 to allow for fine-tuning
        zOrderMap.set(asset.url, index * 10);
      }
    });
  });

  return zOrderMap;
};

/**
 * Sorts assets by their z-order (for proper layer stacking)
 * @param assets - Assets to sort
 * @param zOrderMap - Map of asset URL to z-index
 * @returns Sorted array of assets
 */
export const sortAssetsByZOrder = (
  assets: Asset[],
  zOrderMap: Map<string, number>
): Asset[] => {
  return [...assets].sort((a, b) => {
    const zA = zOrderMap.get(a.url) ?? 0;
    const zB = zOrderMap.get(b.url) ?? 0;
    return zA - zB;
  });
};

/**
 * Gets all assets that should be hidden (not in current configuration)
 * @param assets - All available assets
 * @param layerConfiguration - Current layer configuration
 * @returns Array of assets that should be hidden
 */
export const getHiddenAssets = (
  assets: Asset[],
  layerConfiguration: LayerConfiguration,
  productView : string,
): Asset[] => {
  const visibleAssets = getVisibleAssets(assets, layerConfiguration, productView);
  const visibleUrls = new Set(visibleAssets.map((asset) => asset.url));

  return assets.filter((asset) => !visibleUrls.has(asset.url));
};

/**
 * Checks if an asset should be visible based on the current configuration
 * @param asset - Asset to check
 * @param layerConfiguration - Current layer configuration
 * @returns True if the asset should be visible
 */
export const isAssetVisible = (
  asset: Asset,
  layerConfiguration: LayerConfiguration,
  optionConfiguration: OptionConfiguration = {},
): boolean => {
  const productView = getProductView(asset);
  const productLayer = getProductLayer(asset);
  const productRef = getProductRef(asset);

  if (!productLayer || !productRef || !productView) {
    return false;
  }

  if (getProductOptionsFromAsset(asset)) {
    const selectedOptions = optionConfiguration[productView]?.[productLayer];
    if (!selectedOptions) {
      return false;
    }

    return findAssetByProductOptions(
      [asset],
      productView,
      productLayer,
      selectedOptions
    ) !== undefined;
  }

  const configuredRef = layerConfiguration[productView][productLayer];
  return configuredRef === productRef;
};

/**
 * Gets a suggested layer order based on common layer types
 * This can be customized per project
 */
export const getDefaultLayerOrder = (): string[] => {
  return [
    'background',
    'floor',
    'sol',
    'wall',
    'mur',
    'ceiling',
    'plafond',
    'door',
    'porte',
    'window',
    'fenêtre',
    'furniture',
    'meuble',
    'bed',
    'lit',
    'mirror',
    'miroir',
    'light',
    'lumière',
    'accessory',
    'accessoire',
  ];
};
